<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@/composables/useAuth'
import { useEvents } from '@/composables/useEvents'
import type { Event } from '@/types/events'
import OrganizerLayout from '@/components/layout/OrganizerLayout.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import { parseDeletionBlocked, deletionBlockedMessageKey } from '@/utils/deletionBlocked'
import OverflowMenu from '@/components/shared/OverflowMenu.vue'
import HeaderCreateButton from '@/components/shared/HeaderCreateButton.vue'
import PublicLinksCard from '@/components/organizer/events/PublicLinksCard.vue'

const { t } = useI18n()
const { user } = useAuth()
const { events, fetchEvents, deleteEvent } = useEvents()

// Local loading/error state — intentionally NOT using the shared refs from
// useEvents, which are also modified by fetchEvent() calls in sidebar zone
// components. Using the shared refs would cause race conditions where sidebar
// operations bleed into this page's loading display.
const pageLoading = ref(false)
const pageError = ref<string | null>(null)

const confirmDeleteId = ref<string | null>(null)
const confirmDeleteName = ref('')
const deleteLoading = ref(false)
const deleteBlockedMessage = ref<string | null>(null)
const successMessage = ref('')

onMounted(async () => {
  pageLoading.value = true
  pageError.value = null
  try {
    await fetchEvents()
  } catch {
    pageError.value = 'events.list.error_load'
  } finally {
    pageLoading.value = false
  }
})

function requestDelete(event: Event): void {
  confirmDeleteId.value = event.slug
  confirmDeleteName.value = event.name
  successMessage.value = ''
  deleteBlockedMessage.value = null
}

function cancelDelete(): void {
  deleteBlockedMessage.value = null
  confirmDeleteId.value = null
  confirmDeleteName.value = ''
}

async function confirmDelete(): Promise<void> {
  if (confirmDeleteId.value === null) return
  deleteLoading.value = true
  deleteBlockedMessage.value = null
  try {
    await deleteEvent(confirmDeleteId.value)
    successMessage.value = t('events.list.delete_success')
    confirmDeleteId.value = null
    confirmDeleteName.value = ''
  } catch (err: unknown) {
    const blocked = parseDeletionBlocked(err)
    if (blocked === null) throw err
    deleteBlockedMessage.value = t(deletionBlockedMessageKey(blocked.subject))
  } finally {
    deleteLoading.value = false
  }
}

function orgSlug(): string | null {
  return user.value?.organisations[0]?.slug ?? null
}

function formatDateRange(startDate: string, endDate: string): string {
  const start = new Date(startDate)
  const end = new Date(endDate)
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }
  const startStr = start.toLocaleDateString(undefined, opts)
  if (startDate === endDate) return startStr
  const endStr = end.toLocaleDateString(undefined, opts)
  return `${startStr} – ${endStr}`
}

function eventInitial(name: string): string {
  return name.charAt(0).toUpperCase()
}


</script>

<template>
  <OrganizerLayout>

    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <!-- Page heading -->
      <div class="flex items-center justify-between mb-8">
        <div>
          <h1 class="text-3xl font-bold text-white">{{ t('events.list.heading') }}</h1>
          <p class="text-slate-400 mt-1">{{ t('events.list.subheading') }}</p>
        </div>
        <HeaderCreateButton to="/organizer/events/create" :label="t('events.list.create_button')" />
      </div>

      <!-- Success message -->
      <div
        v-if="successMessage"
        class="mb-6 rounded-xl bg-emerald-500/10 border border-emerald-500/30 px-5 py-3 text-sm text-emerald-400"
      >
        {{ successMessage }}
      </div>

      <!-- Error message -->
      <div
        v-if="pageError"
        class="mb-6 rounded-xl bg-red-500/10 border border-red-500/30 px-5 py-3 text-sm text-red-400"
      >
        {{ t('events.list.error_load') }}
      </div>

      <!-- Skeleton loading state -->
      <div v-if="pageLoading" class="space-y-3">
        <div v-for="i in 4" :key="i" class="rounded-xl bg-slate-800/50 border border-slate-700 px-5 py-4 animate-pulse">
          <div class="flex items-start gap-4">
            <div class="w-12 h-12 rounded-lg bg-slate-700 shrink-0" />
            <div class="flex-1 space-y-2">
              <div class="h-4 bg-slate-700 rounded w-48" />
              <div class="h-3 bg-slate-700 rounded w-72" />
              <div class="flex gap-2 mt-2">
                <div class="h-5 bg-slate-700 rounded-full w-20" />
                <div class="h-5 bg-slate-700 rounded-full w-20" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div
        v-else-if="events.length === 0 && !pageError"
        class="rounded-2xl border border-dashed border-slate-700 bg-slate-800/30 px-8 py-16 text-center"
      >
        <div class="mx-auto mb-4 w-14 h-14 rounded-full bg-slate-800 flex items-center justify-center">
          <svg class="w-7 h-7 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
        <p class="text-slate-400 text-sm mb-6">{{ t('events.list.empty_state') }}</p>
        <RouterLink
          to="/organizer/events/create"
          class="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors"
        >
          {{ t('events.list.create_button') }}
        </RouterLink>
      </div>

      <!-- Enriched event card list -->
      <div v-else-if="events.length > 0" class="space-y-3">
        <article
          v-for="ev in events"
          :key="ev.id"
          class="rounded-xl bg-slate-800/50 border border-slate-700 px-5 py-4 hover:border-slate-600 transition-colors"
        >
          <div class="flex items-start gap-4">
            <!-- Event logo or initial fallback -->
            <div class="shrink-0">
              <img
                v-if="ev.logo_url"
                :src="ev.logo_url"
                :alt="ev.name"
                class="w-12 h-12 rounded-lg object-cover"
              />
              <div
                v-else
                class="w-12 h-12 rounded-lg bg-slate-700 flex items-center justify-center"
              >
                <span class="text-lg font-bold text-slate-300">{{ eventInitial(ev.name) }}</span>
              </div>
            </div>

            <!-- Event info -->
            <div class="flex-1 min-w-0">
              <!-- Name row + maintenance badge -->
              <div class="flex items-center gap-2 flex-wrap">
                <RouterLink
                  :to="`/organizer/events/${ev.slug}`"
                  class="text-sm font-semibold text-white hover:text-emerald-400 transition-colors"
                >
                  {{ ev.name }}
                </RouterLink>
                <span
                  v-if="ev.maintenance"
                  class="shrink-0 inline-flex items-center rounded-full bg-amber-500/15 border border-amber-500/30 px-2.5 py-0.5 text-xs font-medium text-amber-400"
                >
                  {{ t('events.list.maintenance_badge') }}
                </span>
              </div>

              <!-- Active edition metadata -->
              <div v-if="ev.active_edition" class="mt-1">
                <p class="text-xs text-slate-400">
                  {{ ev.active_edition.name }}
                  <span class="mx-1">&middot;</span>
                  {{ formatDateRange(ev.active_edition.start_date, ev.active_edition.end_date) }}
                  <template v-if="ev.active_edition.venue?.name">
                    <span class="mx-1">&middot;</span>
                    {{ ev.active_edition.venue.name }}
                  </template>
                </p>

                <!-- Race status pills -->
                <div
                  v-if="ev.active_edition.races && ev.active_edition.races.length > 0"
                  class="flex flex-wrap gap-2 mt-2"
                >
                  <RouterLink
                    v-for="race in ev.active_edition.races"
                    :key="race.id"
                    :to="`/organizer/events/${ev.slug}/editions/${ev.active_edition.slug}/races/${race.slug}/participants`"
                    class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity"
                    :class="race.registrations_open
                      ? 'bg-emerald-500/15 border border-emerald-500/30 text-emerald-400'
                      : 'bg-slate-700/50 border border-slate-600 text-slate-400'"
                  >
                    <span
                      class="w-1.5 h-1.5 rounded-full"
                      :class="race.registrations_open ? 'bg-emerald-400' : 'bg-slate-500'"
                      aria-hidden="true"
                    />
                    {{ race.name }}
                  </RouterLink>
                </div>
              </div>

              <p v-else class="text-xs text-slate-500 italic mt-1">
                {{ t('events.list.no_active_edition') }}
              </p>

              <!-- Public links -->
              <div class="mt-3">
                <PublicLinksCard :event="ev" :org-slug="orgSlug()" />
              </div>
            </div>

            <!-- Right side: Manage button + overflow menu -->
            <div class="shrink-0 flex items-center gap-2">
              <RouterLink
                :to="`/organizer/events/${ev.slug}`"
                class="hidden sm:inline-flex text-xs font-medium text-emerald-400 hover:text-emerald-300 transition-colors px-3 py-1.5 rounded-lg border border-emerald-500/30 hover:border-emerald-500/60"
              >
                {{ t('events.list.action_manage') }}
              </RouterLink>

              <!-- Overflow menu -->
              <OverflowMenu>
                <!-- Mobile-only: below sm, the Manage button is hidden, so it is
                     duplicated here (sm:hidden) to keep the action reachable. -->
                <RouterLink
                  :to="`/organizer/events/${ev.slug}`"
                  class="sm:hidden w-full text-left px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700/50 transition-colors flex items-center"
                >
                  {{ t('events.list.action_manage') }}
                </RouterLink>
                <hr class="sm:hidden border-slate-700 my-1" />

                <RouterLink
                  :to="`/organizer/events/${ev.slug}/settings`"
                  class="w-full text-left px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700/50 transition-colors flex items-center"
                >
                  {{ t('events.list.action_edit') }}
                </RouterLink>
                <hr class="border-slate-700 my-1" />
                <button
                  type="button"
                  class="w-full text-left px-4 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-slate-700/50 transition-colors"
                  @click="requestDelete(ev)"
                >
                  {{ t('events.list.action_delete') }}
                </button>
              </OverflowMenu>
            </div>
          </div>
        </article>
      </div>

      <ConfirmDialog
        :open="confirmDeleteId !== null"
        :title="t('events.list.confirm_delete_title')"
        :body="t('events.list.confirm_delete_body', { name: confirmDeleteName })"
        :confirm-label="t('events.list.confirm_delete_confirm')"
        :loading="deleteLoading"
        @update:open="cancelDelete"
        @confirm="confirmDelete"
      >
        <template v-if="deleteBlockedMessage" #warning>
          {{ deleteBlockedMessage }}
        </template>
      </ConfirmDialog>
    </main>
  </OrganizerLayout>
</template>
